import {
  Box,
  Paper,
  Typography,
  IconButton,
  useTheme,
  Stack,
  Chip,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CircleIcon from "@mui/icons-material/Circle";
import { tokens } from "../../../theme";
import { useState } from "react";
import { addDays, format, isToday, startOfWeek, isSameWeek } from "date-fns";
import type { OpsWeeklyScheduleItem } from "../types/opsDashboard";
import { formatDateForDisplay } from "../../../utils/FormatDate";

interface CalendarDay {
  weekday: string;
  dayNum: number;
  date: Date;
  isActive?: boolean;
}

function getWeekDays(referenceDate: Date): CalendarDay[] {
  const start = startOfWeek(referenceDate, { weekStartsOn: 1 }); // Monday
  return Array.from({ length: 7 }, (_, i) => {
    const date = addDays(start, i);
    return {
      weekday: format(date, "EEE"),
      dayNum: date.getDate(),
      date,
      isActive: isToday(date),
    };
  });
}

interface WeeklyScheduleWidgetProps {
  schedule?: OpsWeeklyScheduleItem[];
}

export default function WeeklyScheduleWidget({
  schedule = [],
}: WeeklyScheduleWidgetProps) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [referenceDate, setReferenceDate] = useState(new Date());
  const days = getWeekDays(referenceDate);
  const weekLabel = `${format(days[0].date, "MMM d")} – ${format(days[6].date, "MMM d")}`;
  const primaryGradient = `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`;

  const isCurrentWeek = isSameWeek(referenceDate, new Date(), {
    weekStartsOn: 1,
  });

  const visibleItems = isCurrentWeek
    ? schedule.filter((item) => {
        const itemDate = new Date(item.date);
        return days.some(
          (d) => format(d.date, "yyyy-MM-dd") === format(itemDate, "yyyy-MM-dd")
        );
      })
    : [];

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: "24px",
        backgroundColor: colors.primary[900],
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700} color={colors.primary[700]}>
            Weekly Schedule
          </Typography>
          <Typography variant="caption" color={colors.primary[100]} fontWeight={500}>
            {weekLabel}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <IconButton
            size="small"
            onClick={() => setReferenceDate((d) => addDays(d, -7))}
            sx={{ bgcolor: colors.primary[800], color: colors.primary[700] }}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => setReferenceDate((d) => addDays(d, 7))}
            sx={{ bgcolor: colors.primary[800], color: colors.primary[700] }}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mt: 2,
          mb: 3,
          gap: 0.5,
        }}
      >
        {days.map((day, idx) => (
          <Box
            key={idx}
            sx={{
              flex: 1,
              py: 1,
              borderRadius: "10px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 0.5,
              background: day.isActive ? primaryGradient : colors.primary[800],
              color: day.isActive ? "#ffffff" : colors.primary[700],
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontSize: "0.65rem", opacity: day.isActive ? 0.9 : 0.6, fontWeight: 600 }}
            >
              {day.weekday}
            </Typography>
            <Typography variant="body2" fontWeight={700}>
              {day.dayNum}
            </Typography>
          </Box>
        ))}
      </Box>

      <Stack spacing={2}>
        {visibleItems.length === 0 ? (
          <Typography
            variant="body2"
            color={colors.primary[400]}
            textAlign="center"
            py={2}
          >
            No streams scheduled this week
          </Typography>
        ) : (
          visibleItems.map((item) => {
            const isLive = item.live_status === "live";
            const isEnded = item.live_status === "ended";
            const borderColor = isLive ? "#ff4d6d" : isEnded ? colors.primary[600] : "#ffb703";
            const dotColor = isLive ? "#ff4d6d" : isEnded ? colors.primary[500] : "#ffb703";
            const chipLabel = isLive ? "Live" : isEnded ? "Ended" : "Upcoming";

            return (
              <Box
                key={item.id}
                sx={{
                  p: 2,
                  borderRadius: "16px",
                  border: `1px solid ${borderColor}`,
                  bgcolor: colors.primary[900],
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <CircleIcon sx={{ fontSize: 8, color: dotColor }} />
                    <Typography variant="body2" fontWeight={700} color={colors.primary[100]}>
                      {item.title}
                    </Typography>
                  </Box>
                  <Typography
                    variant="caption"
                    color={colors.primary[400]}
                    sx={{ pl: 1.5, display: "block", mt: 0.25 }}
                  >
                    {item.city}
                    {item.date ? ` · ${formatDateForDisplay(new Date(item.date))}` : ""}
                    {item.start ? ` · ${item.start}` : ""}
                  </Typography>
                </Box>

                <Chip
                  label={chipLabel}
                  size="small"
                  sx={{
                    height: "20px",
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    borderRadius: "12px",
                    bgcolor: isLive ? "#fff0f3" : isEnded ? colors.primary[800] : "#fffbeb",
                    color: isLive ? "#ff4d6d" : isEnded ? colors.primary[400] : "#b45309",
                  }}
                />
              </Box>
            );
          })
        )}
      </Stack>
    </Paper>
  );
}
